import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Shell, Section, KpiCard, StatusPill } from "@/components/loomi/Layout";
import { ADMIN_NAV } from "./AdminDashboard";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Save, ArrowLeft, Plus, X } from "lucide-react";
import { CopyField } from "@/components/loomi/CopyField";

export default function AdminPartnerDetail() {
  const { pid } = useParams();
  const [p, setP] = useState(null);
  const [projects, setProjects] = useState([]);
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const load = async () => {
    const [pr, prj, ev] = await Promise.all([
      api.get(`/admin/partners/${pid}`),
      api.get(`/admin/partners/${pid}/projects`),
      api.get(`/admin/partners/${pid}/events`),
    ]);
    setP(pr.data); setProjects(prj.data); setEvents(ev.data);
    setForm({
      name: pr.data.name, base_url: pr.data.base_url, api_key: "",
      supplier_id: pr.data.supplier_id || "",
      feed_path: pr.data.feed_path, auth_scheme: pr.data.auth_scheme,
      auth_header_name: pr.data.auth_header_name || "",
    });
  };
  useEffect(() => { load(); }, [pid]);

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      // Only send api_key if user typed something
      const body = { ...form };
      if (!body.api_key) delete body.api_key;
      await api.patch(`/admin/partners/${pid}`, body);
      toast.success("Saved");
      load();
    } catch (err) { toast.error("Failed", { description: formatApiError(err) }); }
    finally { setSaving(false); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/admin/partners/${pid}/sync`);
      toast.success(`Fetched ${data.fetched} · created ${data.created.length} · updated ${data.updated.length}`);
      load();
    } catch (err) { toast.error("Sync failed", { description: formatApiError(err) }); }
    finally { setSyncing(false); }
  };

  if (!p) return <Shell title="Loading partner…" portalLabel="Admin" nav={ADMIN_NAV}><div className="text-mute">Loading…</div></Shell>;

  const totalCounters = projects.reduce((acc, x) => {
    const c = x.counters || {};
    acc.starts += c.starts || 0; acc.complete += c.complete || 0; acc.terminate += c.terminate || 0;
    return acc;
  }, { starts: 0, complete: 0, terminate: 0 });

  return (
    <Shell title={p.name} portalLabel="Admin" nav={ADMIN_NAV}>
      <div className="mb-6 text-xs text-mute">
        <Link to="/admin/partners" className="hover:text-white inline-flex items-center gap-1" data-testid="partner-back-link">
          <ArrowLeft className="w-3 h-3" /> back to partners
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <KpiCard label="Projects" value={projects.length} accent="primary" testId="pd-projects" />
        <KpiCard label="Starts" value={totalCounters.starts} accent="blue" testId="pd-starts" />
        <KpiCard label="Completes" value={totalCounters.complete} accent="green" testId="pd-completes" />
        <KpiCard label="Terminates" value={totalCounters.terminate} accent="red" testId="pd-terminates" />
        <KpiCard label="Kind" value={p.kind?.toUpperCase()} accent="amber" testId="pd-kind" />
      </div>

      <div className="grid md:grid-cols-3 gap-6 mt-8">
        <form onSubmit={save} className="md:col-span-2 glass rounded-2xl p-6" data-testid="partner-config-form">
          <h3 className="font-display font-bold text-lg mb-4">Configuration</h3>
          <div className="grid grid-cols-2 gap-3">
            <F label="Name" wide><input required value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pd-name" className="in" /></F>
            <F label="Base URL" wide><input required value={form.base_url || ""} onChange={(e) => setForm({ ...form, base_url: e.target.value })} data-testid="pd-base-url" className="in font-mono" /></F>
            <F label="API key (leave blank to keep)" wide><input type="password" placeholder={`current: …${p.api_key_last4 || "?"}`} value={form.api_key || ""} onChange={(e) => setForm({ ...form, api_key: e.target.value })} data-testid="pd-api-key" className="in font-mono" /></F>
            <F label="Supplier ID"><input value={form.supplier_id || ""} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })} data-testid="pd-supplier-id" className="in font-mono" /></F>
            <F label="Feed path"><input value={form.feed_path || ""} onChange={(e) => setForm({ ...form, feed_path: e.target.value })} data-testid="pd-feed-path" className="in font-mono" /></F>
            <F label="Auth scheme">
              <select value={form.auth_scheme || "header"} onChange={(e) => setForm({ ...form, auth_scheme: e.target.value })} data-testid="pd-auth-scheme" className="in">
                <option value="header">Custom header</option><option value="bearer">Bearer token</option><option value="query">Query param</option>
              </select>
            </F>
            <F label="Header name"><input value={form.auth_header_name || ""} onChange={(e) => setForm({ ...form, auth_header_name: e.target.value })} data-testid="pd-auth-header" className="in font-mono" /></F>
          </div>
          <div className="mt-4 flex gap-2">
            <button disabled={saving} data-testid="pd-save" className="bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 rounded-md py-2 px-4 text-sm font-medium inline-flex items-center gap-2">
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
            </button>
            <button type="button" onClick={sync} disabled={syncing} data-testid="pd-sync" className="bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 rounded-md py-2 px-4 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60">
              <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync opportunities"}
            </button>
            <button type="button" onClick={() => setAddOpen(true)} data-testid="pd-add-manual" className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-md py-2 px-4 text-sm inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add manually
            </button>
          </div>
          <style>{`.in{width:100%;background:rgba(0,0,0,0.5);border:1px solid rgba(255,255,255,0.1);border-radius:6px;padding:8px 12px;font-size:14px;color:#fff}.in:focus{outline:none;border-color:#6366f1;box-shadow:0 0 0 1px #6366f1}`}</style>
        </form>

        <Section title="Inbound endpoints" testId="section-inbound-endpoints">
          <div className="text-xs text-mute mb-2">Give these to the partner to reach SyncLine:</div>
          <div className="text-[10px] uppercase tracking-widest text-mute mt-2 mb-1">Return URL (browser)</div>
          <CopyField value={`${BACKEND_URL}/api/partners/return?partner_id=${p.id}&sub_id=[SUB_ID]&project_id=[PROJECT_ID]&status=[STATUS]&hash=[HMAC]`} testId="pd-return-url" />
          <div className="text-[10px] uppercase tracking-widest text-mute mt-3 mb-1">Postback URL (server-to-server)</div>
          <CopyField value={`${BACKEND_URL}/api/partners/${p.id}/postback`} testId="pd-postback-url" />
          <div className="text-[11px] text-mute mt-2 leading-relaxed">POST JSON body <code className="font-mono">{`{sub_id, project_id?, status}`}</code>. If a webhook signing secret is set, verify via header <code className="font-mono">X-Signature: sha256=&lt;hex&gt;</code>.</div>
        </Section>
      </div>

      <div className="mt-8">
        <Section title="Partner-sourced projects" testId="section-partner-projects" action={
          <div className="text-xs text-mute">Panelists automatically see these on their dashboard.</div>
        }>
          {projects.length === 0 ? (
            <div className="text-mute text-sm py-8 text-center" data-testid="partner-projects-empty">No opportunities imported yet. Click <b>Sync</b> or <b>Add manually</b>.</div>
          ) : (
            <table className="w-full text-sm" data-testid="partner-projects-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-mute border-b border-white/10">
                  <th className="py-3 pr-4">Name</th><th className="py-3 pr-4">External ID</th><th className="py-3 pr-4 text-right">CPI</th>
                  <th className="py-3 pr-4 text-right">LOI</th><th className="py-3 pr-4 text-right">IR%</th>
                  <th className="py-3 pr-4">Status</th>
                  <th className="py-3 pr-4 text-right">Starts</th><th className="py-3 pr-4 text-right">Complete</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((prj) => (
                  <tr key={prj.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`partner-project-row-${prj.id}`}>
                    <td className="py-3 pr-4"><Link to={`/admin/projects/${prj.id}`} className="font-medium hover:underline">{prj.name}</Link></td>
                    <td className="py-3 pr-4 font-mono text-xs">{prj.partner_external_id || "—"}</td>
                    <td className="py-3 pr-4 text-right font-mono">${(prj.cpi || 0).toFixed(2)}</td>
                    <td className="py-3 pr-4 text-right font-mono">{prj.loi}m</td>
                    <td className="py-3 pr-4 text-right font-mono">{prj.incidence_rate}%</td>
                    <td className="py-3 pr-4"><StatusPill status={prj.status} /></td>
                    <td className="py-3 pr-4 text-right font-mono">{prj.counters?.starts ?? 0}</td>
                    <td className="py-3 pr-4 text-right font-mono status-complete">{prj.counters?.complete ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      </div>

      <div className="mt-8">
        <Section title="Recent partner events (returns + postbacks)" testId="section-partner-events">
          {events.length === 0 ? (
            <div className="text-mute text-sm py-6 text-center" data-testid="partner-events-empty">No inbound events yet.</div>
          ) : (
            <div className="max-h-[380px] overflow-y-auto">
              <table className="w-full text-xs" data-testid="partner-events-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-mute border-b border-white/10">
                    <th className="py-2 pr-3">Time</th><th className="py-2 pr-3">Kind</th><th className="py-2 pr-3">Sub ID</th>
                    <th className="py-2 pr-3">Status</th><th className="py-2 pr-3">Signature</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((e) => (
                    <tr key={e.id} className="border-b border-white/5">
                      <td className="py-2 pr-3 font-mono text-mute">{new Date(e.timestamp).toLocaleTimeString()}</td>
                      <td className="py-2 pr-3 font-mono">{e.kind}</td>
                      <td className="py-2 pr-3 font-mono">{e.sub_id || "—"}</td>
                      <td className="py-2 pr-3"><StatusPill status={e.status} /></td>
                      <td className={`py-2 pr-3 font-mono ${e.signature_ok == null ? "text-mute" : e.signature_ok ? "status-complete" : "status-terminate"}`}>{e.signature_ok == null ? "n/a" : e.signature_ok ? "ok" : "bad"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      </div>

      {addOpen && <ManualOpportunityModal pid={pid} onClose={() => setAddOpen(false)} onCreated={() => { setAddOpen(false); load(); }} />}
    </Shell>
  );
}

function F({ label, wide, children }) {
  return (<div className={wide ? "col-span-2" : ""}>
    <label className="block text-[10px] uppercase tracking-widest text-mute mb-1">{label}</label>
    {children}
  </div>);
}

function ManualOpportunityModal({ pid, onClose, onCreated }) {
  const [f, setF] = useState({ external_id: "", name: "", entry_url: "", cpi: 1.5, loi: 10, incidence_rate: 50, target_n: 100, reward_points: 60 });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post(`/admin/partners/${pid}/opportunities`, {
        ...f, cpi: Number(f.cpi), loi: Number(f.loi),
        incidence_rate: Number(f.incidence_rate), target_n: Number(f.target_n), reward_points: Number(f.reward_points),
      });
      toast.success("Opportunity imported");
      onCreated();
    } catch (err) { toast.error("Failed", { description: formatApiError(err) }); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="manual-opp-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="glass-strong rounded-2xl w-full max-w-lg p-6 relative">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-mute hover:text-white" data-testid="manual-opp-close"><X className="w-4 h-4" /></button>
        <h3 className="font-display font-extrabold text-2xl mb-1">Import an opportunity manually</h3>
        <p className="text-mute text-sm mb-4">Paste the partner opportunity id and entry URL to route your panelists to it right away.</p>
        <div className="grid grid-cols-2 gap-3">
          <F label="External ID" wide><input required data-testid="manual-opp-ext-id" value={f.external_id} onChange={(e) => setF({ ...f, external_id: e.target.value })} className="in font-mono" /></F>
          <F label="Name" wide><input required data-testid="manual-opp-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="in" /></F>
          <F label="Entry URL" wide><input required type="url" data-testid="manual-opp-entry-url" placeholder="https://partner.com/router/entry?opp=XYZ" value={f.entry_url} onChange={(e) => setF({ ...f, entry_url: e.target.value })} className="in font-mono" /></F>
          <F label="CPI $"><input type="number" step="0.01" data-testid="manual-opp-cpi" value={f.cpi} onChange={(e) => setF({ ...f, cpi: e.target.value })} className="in" /></F>
          <F label="LOI (min)"><input type="number" data-testid="manual-opp-loi" value={f.loi} onChange={(e) => setF({ ...f, loi: e.target.value })} className="in" /></F>
          <F label="Incidence %"><input type="number" data-testid="manual-opp-ir" value={f.incidence_rate} onChange={(e) => setF({ ...f, incidence_rate: e.target.value })} className="in" /></F>
          <F label="Target N"><input type="number" data-testid="manual-opp-target" value={f.target_n} onChange={(e) => setF({ ...f, target_n: e.target.value })} className="in" /></F>
          <F label="Panelist reward pts" wide><input type="number" data-testid="manual-opp-reward" value={f.reward_points} onChange={(e) => setF({ ...f, reward_points: e.target.value })} className="in" /></F>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} data-testid="manual-opp-cancel" className="px-4 py-2 rounded-md text-sm bg-white/5 hover:bg-white/10 border border-white/10">Cancel</button>
          <button disabled={saving} data-testid="manual-opp-submit" className="px-4 py-2 rounded-md text-sm bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60">{saving ? "Importing…" : "Import opportunity"}</button>
        </div>
        <style>{`.in{width:100%;background:rgba(0,0,0,0.5);border:1px solid rgba(255,255,255,0.1);border-radius:6px;padding:8px 12px;font-size:14px;color:#fff}.in:focus{outline:none;border-color:#6366f1;box-shadow:0 0 0 1px #6366f1}`}</style>
      </form>
    </div>
  );
}
